import { useEffect, useRef, useState } from "react";
import { Radar, AudioLines, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { fmtMs, plainCodes } from "@/lib/timecodes";

export const parseMentions = (raw) => (raw || "").split(/[,\n;]+/).map((t) => t.trim()).filter(Boolean);
export const MIN_MENTIONS = 5;
const DEFAULT_TERM = "Lucky Rebel";

export const useScanJob = (statusPath) => {
  const [job, setJob] = useState(null);
  const timer = useRef(null);
  const stop = () => { if (timer.current) clearInterval(timer.current); timer.current = null; };
  const track = (id, onDone) => {
    stop();
    timer.current = setInterval(async () => {
      try {
        const { data } = await api.get(`${statusPath}/${id}`);
        setJob(data);
        if (data.status === "done") { stop(); onDone?.(data); }
      } catch { stop(); }
    }, 4000);
  };
  useEffect(() => stop, []);
  return { job, setJob, track, running: job?.status === "running" };
};

export const AdMentionsField = ({ value, onChange }) => {
  const codes = parseMentions(value);
  return (
    <div data-testid="ad-mentions-field">
      <label className="text-sm font-medium leading-none">Ad mention timecodes <span className="text-slate-500">(not published — for the Underdog team)</span></label>
      <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={2} placeholder="0:00, 2:12, 3:23, 5:01, 7:45" className="mt-1.5 w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm font-mono" data-testid="ad-mentions-input" />
      <p className={`mt-1 text-right text-[11px] ${codes.length && codes.length < MIN_MENTIONS ? "text-amber-300" : codes.length >= MIN_MENTIONS ? "text-emerald-400" : "text-slate-500"}`} data-testid="ad-mentions-count">{codes.length} mention{codes.length === 1 ? "" : "s"}{codes.length ? ` · ${codes.join(", ")}` : ""}{codes.length && codes.length < MIN_MENTIONS ? ` · add ${MIN_MENTIONS - codes.length} more (${MIN_MENTIONS} required when filled in)` : ""}</p>
    </div>
  );
};

export const KeywordToolCard = ({ onImport, audio, submissionId, title }) => {
  const [scan, setScan] = useState(null); // in-app Keyword Finder job
  const [termMode, setTermMode] = useState("default");
  const [custom, setCustom] = useState("");
  const terms = termMode === "default" ? [DEFAULT_TERM] : custom.split(/[,\n]/).map((t) => t.trim()).filter(Boolean);
  const termLabel = terms.join(", ") || "…";
  const timer = useRef(null);
  useEffect(() => () => clearInterval(timer.current), []);
  const hasAudio = submissionId || (audio?.upload_id && audio.upload_id !== "existing");
  const finish = (job) => {
    setScan(job);
    const hits = job.results.flatMap((r) => r.hits);
    const label = job.terms.join(", ");
    if (hits.length) { onImport(plainCodes(hits)); toast.success(`${hits.length} “${label}” mention${hits.length === 1 ? "" : "s"} found and added to the form`); }
    else toast.info(`“${label}” isn't said anywhere in this audio`);
  };
  const findMentions = async () => {
    if (!hasAudio) return toast.error("Upload the episode audio first, then run Keyword Finder");
    if (!terms.length) return toast.error("Type the sponsor's name or phrase to look for");
    setScan({ status: "queued", results: [] });
    try {
      const body = submissionId ? { submission_id: submissionId } : { upload_id: audio.upload_id };
      const { data: job } = await api.post("/portal/wordfinder/jobs", { ...body, terms, label: title || "" });
      if (job.status === "done") return finish(job);
      setScan(job);
      clearInterval(timer.current);
      timer.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/portal/wordfinder/jobs/${job.id}`);
          if (data.status === "done") { clearInterval(timer.current); finish(data); }
          else if (data.status === "failed") { clearInterval(timer.current); setScan(data); toast.error(data.error || "Keyword Finder couldn't process this audio"); }
        } catch { clearInterval(timer.current); setScan(null); }
      }, 4000);
    } catch (e) { setScan(null); toast.error(errorMessage(e)); }
  };
  const listening = scan && !["done", "failed"].includes(scan.status);
  const hits = scan?.status === "done" ? scan.results.flatMap((r) => r.hits) : [];


  return (
    <div className="card-surface rounded-3xl p-5 pb-7" data-testid="keyword-tool-card">
      <p className="eyebrow mb-1 flex items-center gap-1.5"><Radar size={12} /> Keyword Finder</p>
      <p className="text-[11px] text-slate-500 leading-relaxed">Listens to your uploaded audio and finds every sponsor mention to the second — plurals, run-together and slightly misspoken versions included — then drops the timecodes into the form.</p>
      <div className="mt-3 flex gap-1.5" role="radiogroup" data-testid="keyword-finder-term-mode">
        <button type="button" role="radio" aria-checked={termMode === "default"} onClick={() => setTermMode("default")} className={`rounded-full px-3 py-1 text-[11px] font-semibold ${termMode === "default" ? "bg-emerald-400 text-[#07160F]" : "bg-white/5 text-slate-300 hover:bg-white/10"}`} data-testid="keyword-finder-term-default">{DEFAULT_TERM}</button>
        <button type="button" role="radio" aria-checked={termMode === "custom"} onClick={() => setTermMode("custom")} className={`rounded-full px-3 py-1 text-[11px] font-semibold ${termMode === "custom" ? "bg-emerald-400 text-[#07160F]" : "bg-white/5 text-slate-300 hover:bg-white/10"}`} data-testid="keyword-finder-term-custom">Custom Search</button>
      </div>
      {termMode === "custom" && <Input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Sponsor name or phrase (commas for several)" className="mt-2 h-8 bg-white/5 border-white/10 text-xs" data-testid="keyword-finder-custom-input" />}
      <Button type="button" onClick={findMentions} disabled={!!listening} variant="outline" size="sm" className="mt-3 w-full rounded-full border-emerald-400/30 bg-emerald-400/10 text-emerald-300 hover:bg-emerald-400/20" data-testid="ad-mentions-tool-button">
        {listening ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <AudioLines size={13} className="mr-1.5" />} <span className="truncate">{listening ? "Listening to your audio…" : `Find “${termLabel}” mentions`}</span>
      </Button>
      {scan?.status === "done" && (
        <div className="mt-3 rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid="keyword-finder-result">
          <p className="text-xs font-semibold">{hits.length} mention{hits.length === 1 ? "" : "s"} found{scan.reused ? " (from an earlier listen)" : ""}</p>
          {hits.length > 0 && <p className="mt-1 text-[11px] font-mono text-emerald-300 break-words">{hits.map((h) => fmtMs(h.start, false)).join(", ")}</p>}
          {hits.some((h) => h.match && h.match !== "exact") && <p className="mt-1 text-[10px] text-amber-300">Some hits were variants or sounded close — give them a listen.</p>}
        </div>
      )}
      {scan?.status === "failed" && <p className="mt-2 text-[11px] text-rose-300" data-testid="keyword-finder-error">{scan.error}</p>}
      <p className="mt-3 text-[10px] text-slate-500 text-center" data-testid="keyword-tool-hint">Found timecodes land in the Ad mention field on the left.</p>
    </div>
  );
};
